import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import AdminLayout from "../../layouts/AdminLayout";
import { PageHeader, SearchInput, StatusBadge } from "./components";

function ReservedYachts() {
    const [reservations, setReservations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("Tümü");
    const [selectedReservation, setSelectedReservation] = useState(null);

    // Para birimi formatla
    const formatCurrency = (amount, currency) => {
        const currencySymbols = {
            "0": "€",
            "1": "$",
            "2": "£",
            "3": "₺"
        };
        const symbol = currencySymbols[currency] || "€";
        return `${symbol}${parseFloat(amount).toFixed(2)}`;
    };

    // Tarih formatla
    const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString("tr-TR", {
            year: "numeric",
            month: "long",
            day: "numeric"
        });
    };

    // Rezervasyonları çek
    const fetchReservations = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");

            if (!token) {
                throw new Error("Giriş yapmanız gerekiyor");
            }

            const response = await fetch("/rest/Reservations/getAllReservations", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error("Oturum süresi dolmuş. Lütfen tekrar giriş yapın.");
                }
                throw new Error("Rezervasyonlar getirilemedi");
            }

            const data = await response.json();
            console.log("Rezervasyon verileri:", data);
            setReservations(data);
            setError(null);

        } catch (error) {
            console.error("Rezervasyonlar yüklenirken hata:", error);
            setError(error.message);
            setReservations([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReservations();
    }, []);

    // Modal açıkken body scroll'unu kapat
    useEffect(() => {
        if (selectedReservation) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [selectedReservation]);

    // ESC tuşu ile modalı kapat
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && selectedReservation) {
                setSelectedReservation(null);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [selectedReservation]);

    // Durum rengini belirle
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case "pending":
                return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
            case "confirmed":
                return "bg-green-500/20 text-green-400 border-green-500/30";
            case "completed":
                return "bg-blue-500/20 text-blue-400 border-blue-500/30";
            case "cancelled":
                return "bg-red-500/20 text-red-400 border-red-500/30";
            default:
                return "bg-gray-500/20 text-gray-400 border-gray-500/30";
        }
    };

    // Durum metni
    const getStatusText = (status) => {
        const statusMap = {
            "pending": "Beklemede",
            "confirmed": "Onaylandı",
            "completed": "Tamamlandı",
            "cancelled": "İptal Edildi"
        };
        return statusMap[status?.toLowerCase()] || status;
    };

    // Filtrelenmiş rezervasyonlar
    const filteredReservations = reservations.filter((reservation) => {
        const matchesSearch =
            reservation.yachtName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            reservation.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            reservation.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            reservation.captain?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus =
            statusFilter === "Tümü" || reservation.status?.toLowerCase() === statusFilter.toLowerCase();

        return matchesSearch && matchesStatus;
    });

    // Detay modalını aç/kapa
    const toggleDetails = (reservation) => {
        if (selectedReservation?.id === reservation.id) {
            setSelectedReservation(null);
        } else {
            setSelectedReservation(reservation);
        }
    };

    return (
        <AdminLayout currentPage="reserved-yachts">
            <PageHeader
                title="Rezerve Edilmiş Yatlar"
                subtitle={`Toplam ${filteredReservations.length} rezervasyon`}
            >
                <div className="flex items-center gap-4">
                    <SearchInput
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Rezervasyon ara..."
                    />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500 transition-all duration-300 hover:bg-white/10"
                    >
                        <option value="Tümü">Tüm Durumlar</option>
                        <option value="Pending">Beklemede</option>
                        <option value="Confirmed">Onaylandı</option>
                        <option value="Completed">Tamamlandı</option>
                        <option value="Cancelled">İptal Edildi</option>
                    </select>
                </div>
            </PageHeader>

            {/* Loading Durumu */}
            {loading && (
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
            )}

            {/* Hata Durumu */}
            {error && (
                <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 mb-8 backdrop-blur-sm">
                    <p className="text-red-400">{error}</p>
                </div>
            )}

            {/* Rezervasyon Listesi */}
            {!loading && !error && (
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="text-white/60 border-b border-white/10 bg-white/5">
                                    <th className="text-left px-6 py-4 font-semibold">Yat</th>
                                    <th className="text-left px-6 py-4 font-semibold">Kaptan</th>
                                    <th className="text-left px-6 py-4 font-semibold">Müşteri</th>
                                    <th className="text-left px-6 py-4 font-semibold">Tarih Aralığı</th>
                                    <th className="text-left px-6 py-4 font-semibold">Süre</th>
                                    <th className="text-left px-6 py-4 font-semibold">Tutar</th>
                                    <th className="text-left px-6 py-4 font-semibold">Durum</th>
                                    <th className="text-center px-6 py-4 font-semibold">Detay</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredReservations.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan="8"
                                            className="text-center py-12 text-white/60"
                                        >
                                            <div className="flex flex-col items-center gap-3">
                                                <svg
                                                    className="w-16 h-16 text-white/20"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={1.5}
                                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                    />
                                                </svg>
                                                <p className="text-lg">
                                                    {searchTerm || statusFilter !== "Tümü"
                                                        ? "Arama kriterlerinize uygun rezervasyon bulunamadı"
                                                        : "Henüz rezervasyon bulunmuyor"}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredReservations.map((reservation) => (
                                        <tr
                                            key={reservation.id}
                                            className="border-b border-white/5 hover:bg-white/5 transition-all duration-300 group"
                                        >
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col">
                                                    <span className="font-semibold text-white group-hover:text-blue-300 transition-colors">
                                                        {reservation.yachtName}
                                                    </span>
                                                    {reservation.route && (
                                                        <span className="text-xs text-white/50 mt-1">
                                                            {reservation.route}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                                                        <svg
                                                            className="w-4 h-4 text-blue-400"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <span className="text-white/90">
                                                        {reservation.captain || "Atanmadı"}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col">
                                                    <span className="font-medium text-white">
                                                        {reservation.customerName}
                                                    </span>
                                                    <span className="text-xs text-white/50">
                                                        {reservation.customerEmail}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col gap-1">
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <span className="text-white/90">
                                                            {formatDate(reservation.startDate)}
                                                        </span>
                                                        <span className="text-white/40">→</span>
                                                        <span className="text-white/90">
                                                            {formatDate(reservation.endDate)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs border border-purple-500/30">
                                                    <svg
                                                        className="w-3 h-3"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                    {reservation.duration}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col gap-1">
                                                    <span className="font-semibold text-white">
                                                        {formatCurrency(reservation.totalAmount, reservation.currency)}
                                                    </span>
                                                    <span className="text-xs text-green-400">
                                                        Ödenen: {formatCurrency(reservation.paidAmount, reservation.currency)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span
                                                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                                                        reservation.status
                                                    )}`}
                                                >
                                                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse"></span>
                                                    {getStatusText(reservation.status)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <button
                                                    onClick={() => toggleDetails(reservation)}
                                                    className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-all duration-300 hover:scale-105"
                                                >
                                                    <svg
                                                        className="w-4 h-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                        />
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                                        />
                                                    </svg>
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Detay Modal */}
            {selectedReservation && createPortal(
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
                    <div className="bg-gradient-to-br from-gray-800/95 to-gray-900/95 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl max-w-3xl w-full">
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 border-b border-white/10">
                            <div>
                                <h3 className="text-2xl font-bold text-white">
                                    Rezervasyon Detayları
                                </h3>
                                <p className="text-white/60 mt-1">
                                    {selectedReservation.yachtName}
                                </p>
                            </div>
                            <button
                                onClick={() => setSelectedReservation(null)}
                                className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-6">
                            {/* Yat ve Müşteri Bilgileri */}
                            <div className="grid grid-cols-2 gap-6">
                                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                                    <h4 className="text-white/60 text-sm font-semibold mb-3">Yat Bilgileri</h4>
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-white/60">Yat:</span>
                                            <span className="text-white font-semibold">{selectedReservation.yachtName}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-white/60">Kaptan:</span>
                                            <span className="text-white">{selectedReservation.captain || "Atanmadı"}</span>
                                        </div>
                                        {selectedReservation.route && (
                                            <div className="flex justify-between">
                                                <span className="text-white/60">Rota:</span>
                                                <span className="text-white">{selectedReservation.route}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                                    <h4 className="text-white/60 text-sm font-semibold mb-3">Müşteri Bilgileri</h4>
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-white/60">Ad Soyad:</span>
                                            <span className="text-white font-semibold">{selectedReservation.customerName}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-white/60">E-posta:</span>
                                            <span className="text-white text-sm">{selectedReservation.customerEmail}</span>
                                        </div>
                                        {selectedReservation.customerPhone && (
                                            <div className="flex justify-between">
                                                <span className="text-white/60">Telefon:</span>
                                                <span className="text-white">{selectedReservation.customerPhone}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Tarih ve Süre */}
                            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                                <h4 className="text-white/60 text-sm font-semibold mb-3">Rezervasyon Detayları</h4>
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <span className="text-white/60 text-sm block mb-1">Başlangıç</span>
                                        <span className="text-white font-semibold">{formatDate(selectedReservation.startDate)}</span>
                                    </div>
                                    <div>
                                        <span className="text-white/60 text-sm block mb-1">Bitiş</span>
                                        <span className="text-white font-semibold">{formatDate(selectedReservation.endDate)}</span>
                                    </div>
                                    <div>
                                        <span className="text-white/60 text-sm block mb-1">Süre</span>
                                        <span className="text-white font-semibold">{selectedReservation.duration}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Finansal Bilgiler */}
                            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-sm border border-blue-500/20 rounded-xl p-4">
                                <h4 className="text-white/60 text-sm font-semibold mb-3">Finansal Bilgiler</h4>
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <span className="text-white/60 text-sm block mb-1">Toplam Tutar</span>
                                        <span className="text-white font-bold text-lg">
                                            {formatCurrency(selectedReservation.totalAmount, selectedReservation.currency)}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-white/60 text-sm block mb-1">Ödenen</span>
                                        <span className="text-green-400 font-bold text-lg">
                                            {formatCurrency(selectedReservation.paidAmount, selectedReservation.currency)}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-white/60 text-sm block mb-1">Komisyon</span>
                                        <span className="text-yellow-400 font-bold text-lg">
                                            {formatCurrency(selectedReservation.commission, selectedReservation.currency)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Durum */}
                            <div className="flex items-center justify-between bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                                <span className="text-white/60 font-semibold">Rezervasyon Durumu:</span>
                                <span
                                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(
                                        selectedReservation.status
                                    )}`}
                                >
                                    <span className="w-2 h-2 rounded-full bg-current animate-pulse"></span>
                                    {getStatusText(selectedReservation.status)}
                                </span>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10">
                            <button
                                onClick={() => setSelectedReservation(null)}
                                className="px-6 py-2 bg-white/10 border border-white/20 rounded-xl text-white hover:bg-white/15 transition-all duration-300 font-medium"
                            >
                                Kapat
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </AdminLayout>
    );
}

export default ReservedYachts;
